/* eslint-disable array-bracket-newline */
import { useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';
import { Network } from '@kattana-io/common';
import { Address, PublicClient, erc20ABI, usePublicClient } from 'wagmi';
import {
  SendTransactionArgs,
  waitForTransaction,
  readContract,
  prepareWriteContract,
  prepareSendTransaction,
  writeContract,
  sendTransaction,
} from 'wagmi/actions';
import { buildSwapTransaction } from '../helpers/buildTransaction';
import { ICoin } from '../reducers/dex/tradingReducer';
import { actionAppendTransaction } from '../../../actions/transactions';
import { toIntermediateTransaction } from '../../../helpers/intermediateTransactions';
import { toChecksumAddressWrapper } from '../../../helpers/checksumAddress';
import { OnRefresh } from '../../../wallet/walletContext';
import { IPriceRequestResponse } from './useTradeEstimate';

enum swapTexts {
  swap = 'Swap',
  allowance = 'Asking allowance...',
  allowanceMining = 'Mining allowance...',
  txAsking = 'Asking transaction...',
  txMining = 'Mining transaction...',
}

interface MarketTradeProps {
  network: Network;
  base: ICoin;
  quote: ICoin;
  slippage: number;
  userAddress: string;
  priceRoute: IPriceRequestResponse | undefined;
  onNotify: (input: any) => void;
  onWalletRefresh: OnRefresh;
  estimated: boolean;
}

/**
 * Returns the gas value plus a margin for unexpected or variable gas costs
 * @param value the gas value to pad
 */
const calculateGasMargin = (value: bigint = BigInt(0)) =>
  (value * BigInt(120)) / BigInt(100);

const BASE_TOKEN = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';
const MaxUint256 =
  '115792089237316195423570985008687907853269984665640564039457584007913129639935';
/**
 * Check if provider from data has enough allowance
 */
const AskAllowance = async (
  address: string,
  base: ICoin,
  route: IPriceRequestResponse,
  network: number,
  dispatch: any,
  client: PublicClient
): Promise<any> => {
  if (
    toChecksumAddressWrapper(base.address) ===
    toChecksumAddressWrapper(BASE_TOKEN)
  ) {
    // We can't use allowance for base token like ETH, BNB
    return false;
  }

  /**
   * So there are two types of tokens
   * one restricts max approval
   * seconds doesn't care
   */
  let estimatedGas;
  let amount;
  try {
    // @ts-ignore
    const gas = await client.estimateContractGas({
      account: address as Address,
      address: base.address as Address,
      abi: erc20ABI,
      functionName: 'approve',
      args: [route.tokenTransferProxy as Address, BigInt(MaxUint256)],
    });
    estimatedGas = gas;
    amount = MaxUint256;
  } catch (error) {
    console.log('approval: switch to using exact amount');
    // @ts-ignore
    const gas = await client.estimateContractGas({
      account: address as Address,
      address: base.address as Address,
      abi: erc20ABI,
      functionName: 'approve',
      args: [route.tokenTransferProxy as Address, BigInt(route.srcAmount)],
    });
    estimatedGas = gas;
    amount = route.srcAmount;
  }

  const { request: addAllowanceConfig } = await prepareWriteContract({
    address: base.address as Address,
    abi: erc20ABI,
    functionName: 'approve',
    args: [route.tokenTransferProxy as Address, BigInt(amount)],
    gas: calculateGasMargin(estimatedGas),
  });

  const transaction = await writeContract(addAllowanceConfig);
  console.log('approval transacrion: ', transaction);

  dispatch(
    actionAppendTransaction(
      toIntermediateTransaction(transaction.hash, network)
    )
  );
  await waitForTransaction(transaction);
};

/**
 * Check if provider from data has enough allowance
 */
const AllowanceRequired = async (
  address: string,
  base: ICoin,
  route: IPriceRequestResponse
) => {
  if (
    toChecksumAddressWrapper(base.address) ===
    toChecksumAddressWrapper(BASE_TOKEN)
  ) {
    // We can't use allowance for base token like ETH, BNB
    return false;
  }

  const available = await readContract({
    address: base.address as Address,
    abi: erc20ABI,
    functionName: 'allowance',
    args: [address as Address, route.tokenTransferProxy as Address],
  });

  /**
   * If allowed 0 than we can skip other checks
   */
  if (available.toString() === '0') {
    return true;
  }
  const required = BigInt(route.srcAmount);

  console.log(
    base.address,
    ' allowed: ',
    available.toString(),
    ' required: ',
    required.toString()
  );
  /**
   * if required > available => approval required
   * required < available => approval NOT required
   */
  return required > available;
};

/**
 * Actual trade itself
 * @returns
 */
const useMarketTrade = ({
  base,
  quote,
  network,
  slippage,
  userAddress,
  priceRoute,
  onNotify,
  onWalletRefresh,
}: MarketTradeProps) => {
  const dispatch = useDispatch();
  const client = usePublicClient({ chainId: network.toDecimal() });

  const [loading, setLoading] = useState<boolean>(false);
  const [swapText, setSwapText] = useState<string>(swapTexts.swap);
  const [allowanceRequired, setAllowanceRequired] = useState(false);

  useEffect(() => {
    if (priceRoute && !base.amount.isZero()) {
      AllowanceRequired(userAddress, base, priceRoute).then((required) => {
        if (required) {
          setAllowanceRequired(true);
        } else {
          setAllowanceRequired(false);
        }
      });
    } else {
      setAllowanceRequired(false);
    }
  }, [userAddress, base, priceRoute]);

  const setAllowance = async () => {
    try {
      await AskAllowance(
        userAddress,
        base,
        priceRoute!,
        network.toDecimal(),
        dispatch,
        client
      );
      return { error: false };
    } catch (e: any) {
      console.warn('Transaction failed: ' + e.message);
      if (e.reason) {
        onNotify({ kind: 'warning', message: e.reason });
      } else {
        onNotify({ kind: 'warning', message: 'transaction failed' });
      }
      return { error: true };
    }
  };

  /**
   * Refetch wallet balances after swap completed
   */
  const resetState = () => {
    setLoading(false);
    setSwapText(swapTexts.swap);
    onWalletRefresh([base.address, quote.address]);
  };

  /**
   * Actual swap code
   */
  const onSwap = async () => {
    if (loading || !priceRoute) {
      return;
    }
    setLoading(true);

    setSwapText(swapTexts.allowance);
    const required = await AllowanceRequired(userAddress, base, priceRoute);
    if (required) {
      onNotify({ message: 'Not enough allowance' });
      setSwapText(swapTexts.allowanceMining);
      const data = await setAllowance();
      if (data.error) {
        setLoading(false);
        setSwapText(swapTexts.swap);
        return;
      }
    }
    setSwapText(swapTexts.txAsking);
    const { data, error } = await buildSwapTransaction({
      base,
      quote,
      network,
      slippage,
      userAddress,
      priceRoute,
    });
    if (error) {
      onNotify({ kind: 'warning', message: error });
      setLoading(false);
      setSwapText(swapTexts.swap);
      return;
    } else {
      try {
        const tx: SendTransactionArgs = {
          account: data.from,
          chainId: data.chainId,
          gas: BigInt(data.gas),
          gasPrice: BigInt(data.gasPrice),
          to: data.to,
          data: data.data,
          value: BigInt(data.value),
        };

        const swapConfig = await prepareSendTransaction(tx);
        const transaction = await sendTransaction(swapConfig);
        dispatch(
          actionAppendTransaction(
            toIntermediateTransaction(
              transaction.hash,
              network.toDecimal(),
              true
            )
          )
        );
        console.log('Raw transaction: ', transaction);
        onNotify({ message: 'Transaction submitted, waiting confirmations' });
        setSwapText(swapTexts.txMining);
        const receipt = await waitForTransaction(transaction);
        console.log('Transaction receipt: ', receipt);
        onNotify({ message: 'Swap completed' });
        resetState();
      } catch (err) {
        console.error('Error', err);
        onNotify({ kind: 'warning', message: 'Could not swap, error occured' });
        resetState();
      }
    }
  };

  const handleAllowance = async () => {
    setLoading(true);
    setSwapText(swapTexts.allowanceMining);
    await setAllowance();
    setSwapText(swapTexts.swap);
    setLoading(false);
  };

  return {
    loading,
    onSwap,
    swapText,
    handleAllowance: allowanceRequired ? handleAllowance : undefined,
  };
};

export default useMarketTrade;
